// PdfUtils.js - Utilitário para geração de PDFs dinâmicos
import { jsPDF } from 'jspdf';
import 'jspdf-autotable';
import StringUtils from './StringUtils';

// Função para obter cor baseada no tema atual
const obterCorTema = () => {
    const body = document.body;
    if (body.classList.contains('theme-evo')) {
        return [5, 102, 57]; // Verde escuro EVO: #056639
    } else if (body.classList.contains('theme-tecnoponto')) {
        return [41, 86, 128]; // Azul escuro TECNOPONTO: #295680
    }
    return [51, 121, 188]; // Azul padrão DIXI: #3379BC
};

class PdfUtils {
    static gerarPdfDinamico(titulo = "Relatório sem título", colunas = [], dados = [], opcoesAdicionais = {}) {

        const pdf = new jsPDF({
        orientation: 'portrait',
        unit: 'mm',
        format: 'a4',
        putOnlyUsedFonts: true,
        floatPrecision: 16
        });

        const opcoes = {
            margemEsquerda: 10,
            margemDireita: 10,
            margemTopo: 20,
            logoWidth: 40,
            logoHeight: 20,
            cabecalhoEmpresa: null,
            adicionarDataHora: true,
            nomeArquivo: 'relatorio.pdf',
            ...opcoesAdicionais
        };

        const inicioTabelaY = PdfUtils.adicionarCabecalho(pdf, titulo, opcoes);
        PdfUtils.adicionarDataHora(pdf, opcoes);
        PdfUtils.adicionarTabela(inicioTabelaY, pdf, colunas, dados, opcoes);
    
        return pdf;
    }

    /**
     * Inicializa o PDF com as configurações básicas e adiciona o cabeçalho.
     * @param {string} titulo - Título do relatório.
     * @param {Object} opcoesAdicionais - Opções adicionais para o PDF.
     * @returns {{ pdf: jsPDF, inicioTabelaY: number }} Objeto PDF e posição inicial para tabelas.
     */
    static iniciarPdf(titulo = "Relatório sem título", opcoesAdicionais = {}) {
        const pdf = new jsPDF({
            orientation: 'portrait',
            unit: 'mm',
            format: 'a4',
            putOnlyUsedFonts: true,
            floatPrecision: 16
        });

        const opcoes = {
            margemEsquerda: 10,
            margemDireita: 10,
            margemTopo: 20,
            logoWidth: 40,
            logoHeight: 20,
            cabecalhoEmpresa: null,
            adicionarDataHora: true,
            nomeArquivo: 'relatorio.pdf',
            ...opcoesAdicionais
        };

        const inicioTabelaY = PdfUtils.adicionarCabecalho(pdf, titulo, opcoes);
        PdfUtils.adicionarDataHora(pdf, opcoes);

        return { pdf, inicioTabelaY, opcoes };
    }

    /**
     * Adiciona o cabeçalho ao PDF
     */
    static adicionarCabecalho(pdf, titulo, opcoes) {
        let posicaoY = opcoes.margemTopo - 16;
        const larguraPagina = pdf.internal.pageSize.getWidth();

        const logoWidth = 13; 
        const logoHeight = 7; 
        const logoPosicaoY = posicaoY - 2;
        
        try {
            // Verificamos se a opção de logo está habilitada
            if (opcoes.adicionarLogo !== false) {
                // Se um caminho específico for fornecido, usá-lo, caso contrário, usar o padrão
                const logoImagePath = opcoes.logoPath || `${process.env.PUBLIC_URL}/logo192.png`;
                
                pdf.addImage(logoImagePath, 'PNG', opcoes.margemEsquerda, logoPosicaoY, logoWidth * 1.5, logoHeight * 1.5);
            }
        } catch (error) {
            console.error("Erro ao adicionar logo:", error);
            // Se falhar ao adicionar a logo, não interrompe a geração do relatório
        }

        pdf.setFontSize(16);
        pdf.setFont('helvetica', 'bold');
        pdf.setTextColor(0, 0, 0);
        
        // Versão alternativa do método text para compatibilidade
        const xPos = larguraPagina / 2;
        const yPos = posicaoY + 5;
        
        try {
            // Forma 1: Usando options como terceiro parâmetro (jsPDF v2+)
            pdf.text(titulo, xPos, yPos, { align: 'center' });
        } catch (e) {
            try {
                // Forma 2: Usando alinhamento como quarto parâmetro (versões mais antigas)
                pdf.text(xPos, yPos, titulo, null, null, 'center');
            } catch (e2) {
                // Forma 3: Maneira mais simples (compatível com todas as versões)
                const textWidth = pdf.getTextWidth(titulo);
                pdf.text(titulo, xPos - textWidth / 2, yPos);
            }
        }

        pdf.setDrawColor(200, 200, 200);
        pdf.setLineWidth(0.5);
        pdf.line(
            opcoes.margemEsquerda, 
            posicaoY + 10, 
            larguraPagina - opcoes.margemDireita, 
            posicaoY + 10
        );

        return posicaoY + 15;
    }

    /**
     * Adiciona a data e hora no cabeçalho no canto direito superior do PDF
     */
    static adicionarDataHora(pdf, opcoes) {
        // Se a opção estiver explicitamente definida como false, não adicionar data/hora
        if (opcoes.adicionarDataHora === false) return;
        
        const dataAtual = new Date();
        // Formatação direta para garantir valores corretos
        const dia = String(dataAtual.getDate()).padStart(2, '0');
        const mes = String(dataAtual.getMonth() + 1).padStart(2, '0');
        const ano = dataAtual.getFullYear();
        const horas = String(dataAtual.getHours()).padStart(2, '0');
        const minutos = String(dataAtual.getMinutes()).padStart(2, '0');
        const segundos = String(dataAtual.getSeconds()).padStart(2, '0');
        
        const dataFormatada = `${dia}/${mes}/${ano}`;
        const horaFormatada = `${horas}:${minutos}:${segundos}`;

        pdf.setFontSize(10);
        pdf.setFont('helvetica', 'normal');
        pdf.setTextColor(100, 100, 100);
        
        // Versão alternativa do método text para compatibilidade com diferentes versões do jsPDF
        const xPos = pdf.internal.pageSize.getWidth() - opcoes.margemDireita;
        
        // Forma 1: Usando options como terceiro parâmetro (jsPDF v2+)
        try {
            pdf.text(dataFormatada, xPos, opcoes.margemTopo - 14, { align: 'right' });
            pdf.text(horaFormatada, xPos, opcoes.margemTopo - 8, { align: 'right' });
        } catch (e) {
            // Forma 2: Usando alinhamento como quarto parâmetro (versões mais antigas)
            try {
                pdf.text(xPos, opcoes.margemTopo - 14, dataFormatada, null, null, 'right');
                pdf.text(xPos, opcoes.margemTopo - 8, horaFormatada, null, null, 'right');
            } catch (e2) {
                // Forma 3: Maneira mais simples (compatível com todas as versões)
                const textWidthData = pdf.getTextWidth(dataFormatada);
                const textWidthHora = pdf.getTextWidth(horaFormatada);
                
                pdf.text(dataFormatada, xPos - textWidthData, opcoes.margemTopo - 14);
                pdf.text(horaFormatada, xPos - textWidthHora, opcoes.margemTopo - 8);
            }
        }
    }

    /**
     * Adiciona uma tabela ao PDF
     */
    static adicionarTabela(inicioTabelaY, pdf, colunas, dados, opcoes, descritivoTabela = null, textoIndicativo = "") {
        const totalPagesExp = "{total_pages_count_string}";
        const pageWidth = pdf.internal.pageSize.getWidth();
        const tableX = opcoes.margemEsquerda;
        const tableWidth = pageWidth - opcoes.margemEsquerda - opcoes.margemDireita;
        let headerHeight = 10; 

        if (textoIndicativo || descritivoTabela) {
            pdf.setFontSize(10);
            pdf.setFont('helvetica', 'bold');
            pdf.setTextColor(...obterCorTema());
            const textoCompleto = textoIndicativo;
            
            try {
                // Versão compatível com jsPDF v2+
                pdf.text(textoCompleto, tableX, inicioTabelaY + 1);
            } catch (e) {
                // Versão alternativa para versões mais antigas
                pdf.text(tableX, inicioTabelaY + 1, textoCompleto);
            }

            if (descritivoTabela) {
                pdf.setFont('helvetica', 'normal');
                pdf.setTextColor(0, 0, 0);
                
                const xPos = tableX + pdf.getTextWidth(textoCompleto);
                const texto = ` ${descritivoTabela} `;
                
                try {
                    // Versão compatível com jsPDF v2+
                    pdf.text(texto, xPos, inicioTabelaY + 1);
                } catch (e) {
                    // Versão alternativa para versões mais antigas
                    pdf.text(xPos, inicioTabelaY + 1, texto);
                }
            }
            inicioTabelaY += 3; 
        }

        pdf.setFillColor(...obterCorTema()); 

        this.defineArredondamentoBorda(opcoes, tableX, inicioTabelaY, tableWidth, headerHeight, pdf);

        pdf.autoTable({
            startY: inicioTabelaY,
            margin: { top: 0, bottom: 0, left: opcoes.margemEsquerda, right: opcoes.margemDireita },
            theme: 'grid',
            useCss: true,
            tableLineColor: [255, 255, 255],
            headStyles: {
                fillColor: false, 
                textColor: [255, 255, 255],
                halign: 'center',
                valign: 'middle', // alinhamento vertical no cabeçalho
                fontStyle: 'bold',
                lineWidth: 0.3,
                lineColor: [200, 200, 200]
            },
            styles: {
                textColor: [0, 0, 0],
                fontSize: 10,
                halign: 'center',
                valign: 'middle', // alinhamento vertical nas células
                lineWidth: 0.3,
                lineColor: [200, 200, 200],
            },
            columnStyles: colunas.reduce((acc, coluna, index) => {
                // Mescla config padrão com o que vier em opcoes.columnStyles (se houver)
                const custom = (opcoes.columnStyles && opcoes.columnStyles[index]) || {};
                acc[index] = {
                    cellWidth: coluna.largura,
                    overflow: 'linebreak',
                    ...custom
                };
                return acc;
            }, {}),
            head: [colunas],
            body: dados,
            willDrawCell: (data) => {
                const { cell, column, row, section, table } = data;
                const lastColIndex = table.columns.length - 1;
                const lastRowIndex = section === 'body' ? table.body.length - 1 : null;
                const defaultLineWidth = { top: 0.3, left: 0.3, right: 0.3, bottom: 0.3 };

                // Canto superior esquerdo
                if (section === 'head' && row.index === 0 && column.index === 0 && (!opcoes.ladoSemBorda || !opcoes.ladoSemBorda.topLeft)) {
                    cell.styles.lineWidth = { ...defaultLineWidth, top: 0, left: 0 };
                }
                // Canto superior direito
                if (section === 'head' && row.index === 0 && column.index === lastColIndex && (!opcoes.ladoSemBorda || !opcoes.ladoSemBorda.topRight)) {
                    cell.styles.lineWidth = { ...defaultLineWidth, top: 0, right: 0 };
                }
                // Canto inferior esquerdo
                if (section === 'body' && row.index === lastRowIndex && column.index === 0 && (!opcoes.ladoSemBorda || !opcoes.ladoSemBorda.bottomLeft)) {
                    cell.styles.lineWidth = { ...defaultLineWidth, bottom: 0, left: 0 };
                }
                // Canto inferior direito
                if (section === 'body' && row.index === lastRowIndex && column.index === lastColIndex && (!opcoes.ladoSemBorda || !opcoes.ladoSemBorda.bottomRight)) {
                    cell.styles.lineWidth = { ...defaultLineWidth, right: 0, bottom: 0 };
                }
            },
            didDrawCell: (data) => {
                const { cell, column, row, section, table } = data;
                const lastRowIndex = section === 'body' ? table.body.length - 1 : null;

                // Borda dos cantos (opcional)
                if (section === 'body' && row.index === lastRowIndex && column.index === 0) {
                    cell.styles.lineWidth = { bottom: 0, left: 0.3, right: 0.3, top: 0.3 };
                }
            },
            didDrawPage: (data) => {
                const pageSize = pdf.internal.pageSize;
                const pageHeight = pageSize.height || pageSize.getHeight();
                const margemInferior = 10;

                const footerText = `Página ${data.pageNumber} de ${totalPagesExp}`;

                pdf.setFontSize(10);
                pdf.setTextColor(150);
                
                const xPos = pageSize.width + 28;
                const yPos = pageHeight - margemInferior;
                
                try {
                    // Versão compatível com jsPDF v2+
                    pdf.text(footerText, xPos, yPos, { align: 'right' });
                } catch (e) {
                    try {
                        // Versão alternativa para versões mais antigas
                        pdf.text(xPos, yPos, footerText, null, null, 'right');
                    } catch (e2) {
                        // Maneira mais simples (compatível com todas as versões)
                        const textWidth = pdf.getTextWidth(footerText);
                        pdf.text(footerText, xPos - textWidth, yPos);
                    }
                }
            }
        });

        // 🎯 Desenha o contorno externo com canto esquerdo quadrado e canto direito arredondado (tabela inteira)
        const table = pdf.lastAutoTable;
        if (table) {
            const x = opcoes.margemEsquerda;
            const y = inicioTabelaY;
            const width = pageWidth - opcoes.margemEsquerda - opcoes.margemDireita;
            const height = table.finalY - inicioTabelaY;
            const r = 3;

            const esquerdaSemBorda = opcoes.ladoSemBorda?.topLeft || false;

            if (esquerdaSemBorda) {
                pdf.setDrawColor(200, 200, 200);
                pdf.setLineWidth(0.3);
                pdf.roundedRect(x, y, width, height, r, r, 'S');
                pdf.setFillColor(...obterCorTema());
                pdf.rect(x, y, r, r, 'F');
                pdf.setDrawColor(200, 200, 200);
                pdf.setLineWidth(0.3);
                pdf.line(x, y, x + r, y);  
                pdf.line(x, y, x, y + r); 
            } else {
                pdf.roundedRect(x, y, width, height, 3, 3, 'S');
            }
        }

        if (typeof pdf.putTotalPages === 'function') {
            pdf.putTotalPages(totalPagesExp);
        }
    }

    /**
     * Gera e baixa um PDF com tabela dinâmica
     * @returns {jsPDF} Objeto PDF gerado
     */
    static gerarEBaixarPdf(titulo, colunas, dados, opcoesAdicionais = {}) {
        const pdf = this.gerarPdfDinamico(titulo, colunas, dados, opcoesAdicionais);
        pdf.save(opcoesAdicionais.nomeArquivo || 'relatorio.pdf');
        return pdf;
    }

    /**
     * Gera um PDF e retorna um Blob URL para pré-visualização
     * @returns {string} URL do Blob para visualização
     */
    static gerarPreviewPdf(titulo, colunas, dados, opcoesAdicionais = {}) {
        const pdf = this.gerarPdfDinamico(titulo, colunas, dados, opcoesAdicionais);
        const blob = pdf.output('blob');
        return URL.createObjectURL(blob);
    }

    /**
     * Transforma os dados, gera e salva um PDF dinâmico para relatórios
     * @param {string} titulo - Título do relatório
     * @param {Array} colunas - Colunas do relatório
     * @param {Array} linhas - Linhas de dados do relatório
     * @param {Object} opcoesAdicionais - Opções adicionais para o PDF, incluindo o nome do arquivo
     */
    static transformarDadosEGerarPdf(titulo, colunas, linhas, opcoesAdicionais = {}) {
        
        const dados = linhas.map(linha => {
            return Object.values(
                colunas.reduce((linhaTransformada, coluna, index) => {
                    let valor = linha[index];

                    switch (coluna.descricao) {
                        case ColunasRelatorio.DOCUMENTO.descricao:
                            valor = StringUtils.formatarDocumento(valor);
                            break;
                        case ColunasRelatorio.SITUACAO_CADASTRO.descricao:
                            valor = StringUtils.capitalizarFrases(valor);
                            break;
                        default:
                            valor = valor;
                    }

                    linhaTransformada[coluna.titulo] = valor;
                    return linhaTransformada;
                }, {})
            );
        });

        const pdf = this.gerarPdfDinamico(titulo, colunas.map(c => c.titulo), dados, opcoesAdicionais);

        const nomeArquivo = opcoesAdicionais.nomeArquivo || 'relatorio.pdf';
        pdf.save(nomeArquivo);

        return pdf;
    }

    /**
     * Finaliza o PDF gerado e salva ou retorna o Blob URL.
     * @param {jsPDF} pdf - Objeto PDF já inicializado.
     * @param {Object} opcoes - Opções adicionais para o PDF.
     * @returns {string|void} URL do Blob para visualização ou salva o arquivo.
     */
    static finalizarPdf(pdf, opcoes = {}) {
        const nomeArquivo = opcoes.nomeArquivo || 'relatorio.pdf';

        if (opcoes.retornarBlob) {
            const blob = pdf.output('blob');
            return URL.createObjectURL(blob);
        } else {
            pdf.save(nomeArquivo);
        }
    }
    
    /**
     * Define o arredondamento de borda para a tabela
     */
    static defineArredondamentoBorda(opcoes, tableX, inicioTabelaY, tableWidth, headerHeight, pdf) {
        const esquerdaSemBorda = opcoes.ladoSemBorda?.topLeft || false;

        if (esquerdaSemBorda) {
            const r = 3;
            const x = tableX;
            const y = inicioTabelaY;
            const w = tableWidth;
            const h = headerHeight;

            pdf.setFillColor(...obterCorTema());
            pdf.setDrawColor(...obterCorTema());

            pdf.ellipse(x + w - r, y + r, r, r, 'F');
            pdf.ellipse(x + w - r, y + h - r, r, r, 'F');
            pdf.rect(x, y, w - r, r, 'F');
            pdf.rect(x, y + h - r, w - r, r, 'F');
            pdf.rect(x, y + r, w, h - 2 * r, 'F');
        } else {
            pdf.roundedRect(tableX, inicioTabelaY, tableWidth, headerHeight, 3, 3, 'F');
        }
    }
}

/**
 * TipoRelatorio - Enumeração para tipos de relatório
 * @readonly
 * @enum {Object}
 */
class TipoRelatorio {
    static AUDITORIA = "AUDITORIA";
}

/**
 * ColunasRelatorio - Enumeração para colunas de relatório
 * @readonly
 * @enum {Object}
 */
class ColunasRelatorio {
    static NOME = { descricao: "nome", titulo: "Nome", largura: 30 };
    static RAZAO_SOCIAL = { descricao: "razaoSocial", titulo: "Razão Social", largura: 30 };
    static DOCUMENTO = { descricao: "documento", titulo: "Documento", largura: 30 };
    static SITUACAO_CADASTRO = { descricao: "situacaoCadastro", titulo: "Situação Cadastro", largura: 10 };
    static DATA_HORA = { descricao: "data", titulo: "Data", largura: 20 };
    static EMAIL = { descricao: "email", titulo: "Email", largura: 20 };
    static EMPRESA = { descricao: "empresa", titulo: "Empresa", largura: 30 };
    static PLANO = { descricao: "plano", titulo: "Plano", largura: 20 };
    static UNIDADE = { descricao: "unidade", titulo: "Unidade", largura: 30 };
    static LICENCAS_ATIVAS = { descricao: "licencasAtivas", titulo: "Licenças Ativas", largura: 15 };
    static LICENCAS_TOTAIS = { descricao: "licencasTotais", titulo: "Licenças Totais", largura: 15 };
    static IP = { descricao: "ip", titulo: "IP", largura: 15 };
}

/**
 * Filtra as colunas de acordo com as opções selecionadas pelo usuário.
 * @param {Array} colunas - Array de objetos de coluna (pode ser ColunasRelatorio ou colunas da tabela)
 * @param {Array<string>} opcoesSelecionadas - Array de valores das opções selecionadas (ex: ['situacaoCadastro', 'ip'])
 * @returns {Array} - Array de colunas filtradas
 */
function filtrarColunasPorConfiguracao(colunas, opcoesSelecionadas) {
    if (!Array.isArray(colunas) || !Array.isArray(opcoesSelecionadas)) return colunas;
    if (opcoesSelecionadas.length === 0) return colunas;
    return colunas.filter(col => {
        const key = col.campo || col.descricao;
        // Se a coluna não tem campo/descricao, sempre exibe
        if (!key) return true;
        // Se a coluna está nas opções selecionadas, exibe
        return opcoesSelecionadas.includes(key);
    });
}

/**
 * Filtra as colunas removendo as que estão em colunasRemover.
 * @param {Array} colunas - Array de objetos de coluna (pode ser ColunasRelatorio ou colunas da tabela)
 * @param {Array<string>} colunasRemover - Array de descrições/campos das colunas a remover
 * @returns {Array} - Array de colunas filtradas
 */
function filtrarColunasRemovendo(colunas, colunasRemover) {
    if (!Array.isArray(colunas) || !Array.isArray(colunasRemover)) return colunas;
    if (colunasRemover.length === 0) return colunas;
    return colunas.filter(col => {
        const key = col.campo || col.descricao;
        return !colunasRemover.includes(key);
    });
}

export { PdfUtils, TipoRelatorio, ColunasRelatorio, filtrarColunasPorConfiguracao, filtrarColunasRemovendo };
